<script>
import VideoPlayer from '@maison/components/atoms/VideoPlayer.vue';
import PictureLazy from '@maison/components/common/PictureLazy';
import WishlistButton from '@maison/components/wishlist/WishlistButton.vue';
import Product from '@phoenix/mixins/components/common/Product';

export default {
  components: {
    PictureLazy,
    VideoPlayer,
    WishlistButton,
  },

  mixins: [Product],

  props: {
    defaultInWishlistIcon: {
      type: String,
      default: 'heart-alt',
    },
    disableWideStyle: {
      type: Boolean,
      default: false,
    },
    inProductFamily: {
      type: Boolean,
      default: false,
    },
    video: {
      type: Object,
      default: null,
    },
    wide: {
      type: Boolean,
      default: false,
    },
    /**
     * `{ sources: [], src: '', alt: '', imgClass: '', lazy: '' }`
     */
    wideImage: {
      type: Object,
      default: null,
    },
    hoverImage: {
      type: Object,
      default: null,
    },
    wideHoverImage: {
      type: Object,
      default: null,
    },
    backgroundImage: {
      type: Object,
      default: null,
    },
    wideBackgroundImage: {
      type: Object,
      default: null,
    },
    shouldDisplayHoverImage: {
      type: Boolean,
      default: true,
    },
    smallCard: {
      type: Boolean,
      default: false,
    },
    shouldHidePrice: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    shouldDisplayWideTile() {
      return this.wide && !this.disableWideStyle;
    },

    hasActionsSlot() {
      return !!this.$slots.footer;
    },

    _image() {
      return this.shouldDisplayWideTile ? this.wideImage : this.image;
    },

    _hoverImage() {
      return this.shouldDisplayWideTile ? this.wideHoverImage : this.hoverImage;
    },

    _backgroundImage() {
      return this.shouldDisplayWideTile ? this.wideBackgroundImage : this.backgroundImage;
    },

    _shouldDisplayHoverImage() {
      return this._hoverImage && this.shouldDisplayHoverImage;
    },

    productClasses() {
      return [
        'product-card',
        {
          'product-card--small': this.smallCard,
          'product-card--has-hover-media': this.hoverImage !== null && this.shouldDisplayHoverImage,
        },
      ];
    },
  },

  mounted() {
    if (this.hoverImage) {
      const slider = this.$el.querySelector('.product-card-media-content');
      const scrollbar = this.$el.querySelector('.product-card-media-progress');

      if (slider && scrollbar) {
        new Toolkit.SimpleSlider(slider, { scrollbar });
      }
    }
    // We need to add an event listener on the media div because otherwise we won't have the tracking event sent correctly
    // As pointer-events: inherit is obligated for the slider on mobile
    document.getElementById(`${this.$attrs.itemId}-media`).addEventListener('click', this.pushTracking);
  },

  methods: {
    pushTracking() {
      if (this.dataTracking) {
        window.tracking.pushEvent(this.dataTracking);
      }
    },
  },
};
</script>
<template>
  <div
    :id="$attrs.itemId"
    :class="productClasses"
    :data-size-cy="`product-size-${shouldDisplayWideTile ? 'wide' : 'default'}`"
  >
    <a :href="url" :data-tracking="dataTracking ? JSON.stringify(dataTracking) : null" class="product-card-main">
      <div :id="`${$attrs.itemId}-media`" class="product-card-media" style="pointer-events: inherit">
        <div v-if="_backgroundImage">
          <div class="picture product-card-media-background picture--cover">
            <picture-lazy v-bind="_backgroundImage" />
          </div>
        </div>
        <div class="simple-slider product-card-media-content">
          <div class="simple-slider__body">
            <div v-if="video" class="simple-slider__slide">
              <video-player v-bind="video" />
            </div>
            <div v-if="!video" class="simple-slider__slide">
              <div
                class="picture product-card-media-content__item product-card-media-content__item--default picture--cover"
              >
                <picture-lazy v-bind="_image" img-class="lazyload--fade" />
              </div>
            </div>
            <div v-if="_shouldDisplayHoverImage" class="simple-slider__slide">
              <div
                class="picture product-card-media-content__item product-card-media-content__item--hover picture--cover"
              >
                <picture-lazy v-bind="_hoverImage" img-class="lazyload--fade" />
              </div>
            </div>
          </div>
        </div>
        <div v-if="_shouldDisplayHoverImage" class="product-card-media-progress"></div>
      </div>
      <div class="product-card-body">
        <div class="product-card-body__title">
          {{ name }}
        </div>

        <template v-if="description">
          {{ description }}
        </template>

        <div v-if="!shouldHidePrice" class="product-card__price">
          <product-price v-bind="localizedPrice" :should-display-from-price="shouldDisplayFromPrice" />
        </div>
      </div>
    </a>
    <div class="product-card-aside">
      <ul v-if="label" class="list list--mixed list--xs max-sm:hidden" role="presentation">
        <li role="presentation">
          <div class="tag">{{ label }}</div>
        </li>
      </ul>

      <slot name="actions">
        <ul class="actions" role="presentation">
          <li role="presentation">
            <wishlist-button
              v-if="shouldDisplayAddToWishlist"
              :reference="modelReference"
              product-tile
              :name="name"
              :default-in-wishlist-icon="defaultInWishlistIcon"
              :tracking="tracking"
              :data-cy="`wishlist-item-delete-button-${modelReference}`"
            />
          </li>
        </ul>
      </slot>
    </div>
  </div>
</template>
